import React, { useEffect, useState } from 'react';
import { MODE, hasContentSequence, TextReportView, ModeToggleButton } from './useTextReport';
import type { TextReport } from './useTextReport';

// getSopClassHandlerModule.ts's _getDisplaySetsFromSeries attaches these three
// fields at runtime; platform/core's DisplaySet type declares none of them.
// Cast once here, matching the DisplaySetWithSOPClassUID precedent.
interface SRDisplaySet extends AppTypes.DisplaySet {
  isImagingMeasurementReport?: boolean;
  measurements?: unknown[] | null;
  load: () => Promise<void>;
}

const UPSTREAM_SR_MODULE = '@ohif/extension-cornerstone-dicom-sr.viewportModule.dicom-sr';

/**
 * D2. ViewportGrid.tsx's _getViewportComponent does a pure static
 * SOPClassHandlerId match with no customization hook -- modes/basic/src/
 * index.tsx's dicomsr.viewport remap is the only reachable interception point.
 */
export default function SRViewportWrapper(props: withAppTypes) {
  const displaySets = props.displaySets as SRDisplaySet[];
  // AppTypes.Managers declares this optional; getViewportModule.tsx's closure
  // always injects it, so it is present on every render here.
  const extensionManager = props.extensionManager as AppTypes.ExtensionManager;

  // Same single-display-set contract upstream's own SR viewports assert
  // (OHIFCornerstoneSRMeasurementViewport.tsx / OHIFCornerstoneSRTextViewport.tsx).
  if (displaySets.length > 1) {
    throw new Error('SR viewport should only have a single display set');
  }

  const srDisplaySet = displaySets[0];
  // getSopClassHandlerModule.ts's addInstances re-points `instance` at the newest
  // report on each append -- `instances[0]` is the oldest, kept only as a fallback.
  const instance: TextReport | undefined = srDisplaySet.instance ?? srDisplaySet.instances?.[0];

  /**
   * Only sycai's own report-generation backend stamps Manufacturer 'SYCAI' and
   * the 1127xxxx private-tag block useTextReport.tsx's getAllReportProperties
   * already keys off. A plain SR -- e.g. one saved through upstream's own
   * "Create Report" flow -- has neither, and its hydrate/jump-to-measurement
   * flow already works correctly through the standard measurement-tracking
   * path; routing it through this wrapper's INFO/REPORT switch below only
   * gets in the way of that.
   */
  const isSycaiReport =
    instance?.Manufacturer === 'SYCAI' ||
    Object.keys(instance ?? {}).some(key => key.startsWith('1127'));

  const [mode, setMode] = useState(MODE.REPORT);

  /**
   * Plan D-2. measurements stays null until _load resolves it; reading it
   * early as "not empty" reaches upstream's unguarded measurement destructure
   * and permanently blanks the viewport. Keyed to the display set's own
   * identity, not a bare boolean, since a swapped-in SR must not read as loaded.
   */
  const [loadedDisplaySet, setLoadedDisplaySet] = useState<SRDisplaySet | null>(null);
  const [failedDisplaySet, setFailedDisplaySet] = useState<SRDisplaySet | null>(null);

  useEffect(() => {
    if (!isSycaiReport || !srDisplaySet.isImagingMeasurementReport) {
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        if (!srDisplaySet.isLoaded) {
          await srDisplaySet.load();
        }
        if (!cancelled) {
          setLoadedDisplaySet(srDisplaySet);
          setFailedDisplaySet(null);
        }
      } catch (error) {
        console.error('[sycai] SR display set failed to load', error);
        if (!cancelled) {
          setFailedDisplaySet(srDisplaySet);
          setLoadedDisplaySet(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [srDisplaySet, isSycaiReport]);

  if (!isSycaiReport) {
    const moduleEntry = extensionManager.getModuleEntry(UPSTREAM_SR_MODULE) as
      | { component: React.ComponentType<withAppTypes> }
      | undefined;

    if (!moduleEntry) {
      console.error(`[sycai] no module entry registered at ${UPSTREAM_SR_MODULE}`);
      return null;
    }

    const UpstreamSRViewport = moduleEntry.component;
    return <UpstreamSRViewport {...props} />;
  }

  // D8: a true TextSR carries no ContentSequence at all -- forced INFO, no toggle.
  if (!hasContentSequence(instance)) {
    return <TextReportView instance={instance} />;
  }

  if (failedDisplaySet === srDisplaySet) {
    return <TextReportView instance={instance} />;
  }

  const dataLoaded = !srDisplaySet.isImagingMeasurementReport || loadedDisplaySet === srDisplaySet;
  if (!dataLoaded) {
    return null;
  }

  // D5: a measurement-report SR with zero findings -- forced INFO, no toggle.
  // The regression §7.1 check 2 exists to close.
  if (srDisplaySet.isImagingMeasurementReport && srDisplaySet.measurements?.length === 0) {
    return <TextReportView instance={instance} />;
  }

  let content: React.ReactNode;
  if (mode === MODE.INFO) {
    content = <TextReportView instance={instance} />;
  } else {
    // D4: the full registered upstream module entry, rendered as-is -- it is
    // already Suspense-wrapped internally (cornerstone-dicom-sr/src/index.tsx
    // :13-23), so no boundary is added here.
    const moduleEntry = extensionManager.getModuleEntry(UPSTREAM_SR_MODULE) as
      | { component: React.ComponentType<withAppTypes> }
      | undefined;

    if (!moduleEntry) {
      console.error(`[sycai] no module entry registered at ${UPSTREAM_SR_MODULE}`);
      content = null;
    } else {
      const UpstreamSRViewport = moduleEntry.component;
      content = <UpstreamSRViewport {...props} />;
    }
  }

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      <div className="flex justify-center py-1">
        <ModeToggleButton
          currentMode={mode}
          onModeChange={setMode}
        />
      </div>
      <div className="min-h-0 flex-1">{content}</div>
    </div>
  );
}
